import FeatureFlag from "@/domain/enums/FeatureFlag";
import { cn } from "@/lib/utils";
import { isMacOSPlatform, isTauri } from "@/lib/utils/tauriUtils";
import { useFeatureFlag } from "@cs_teamwaydev/mailvista-sdk_react";
import { Mic, MicOff } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";

const DisabledMicContainer = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            "flex h-11 w-11 cursor-not-allowed items-center justify-center opacity-50",
            className,
        )}
        {...props}
    >
        <MicOff className="size-5" />
    </div>
));
DisabledMicContainer.displayName = "DisabledMicContainer";

interface TooltipWrapperProps {
    tooltipText: string;
    children: React.ReactNode;
}

const TooltipWrapper = ({ tooltipText, children }: TooltipWrapperProps) => (
    <TooltipProvider delayDuration={0}>
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent>
                <p>{tooltipText}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

interface MicrophoneButtonProps {
    browserSupportsSpeechRecognition: boolean;
    listening: boolean;
    startListening: () => void;
    stopListening: () => void;
    isMicrophoneAvailable: boolean;
    isDictationDisabled?: boolean;
    className?: string;
    isMicrophoneTemporarilyDisabled?: boolean;
}

const MicrophoneButton = ({
    browserSupportsSpeechRecognition,
    listening,
    startListening,
    stopListening,
    isMicrophoneAvailable,
    isDictationDisabled,
    className,
    isMicrophoneTemporarilyDisabled,
}: MicrophoneButtonProps) => {
    const { t } = useTranslation();
    const isAssistantVocalEnabled = useFeatureFlag(
        FeatureFlag.ASSISTANT_ENABLE_VOCAL,
    );

    if (
        !browserSupportsSpeechRecognition ||
        !isMicrophoneAvailable ||
        !isAssistantVocalEnabled
    ) {
        let tooltipText: string;

        if (!browserSupportsSpeechRecognition) {
            tooltipText = isTauri()
                ? t(
                      isMacOSPlatform()
                          ? "global.audioCapture.tauriUnsupportedMac"
                          : "global.audioCapture.tauriUnsupportedWindows",
                  )
                : t("global.audioCapture.browserNotSupported");
        } else if (!isAssistantVocalEnabled) {
            tooltipText = t("global.audioCapture.featureDisabled");
        } else {
            tooltipText = t("global.audioCapture.notAllowed");
        }

        return (
            <TooltipWrapper tooltipText={tooltipText}>
                <DisabledMicContainer className={className} />
            </TooltipWrapper>
        );
    }

    if (isDictationDisabled) {
        const tooltipText = isMacOSPlatform()
            ? "global.audioCapture.dictationDisabledMac"
            : "global.audioCapture.dictationDisabledWindows";

        return (
            <TooltipWrapper tooltipText={tooltipText}>
                <Button
                    className={cn(
                        "h-11 w-11 rounded-full p-0 [&_svg]:size-5",
                        className,
                    )}
                    onClick={(e) => {
                        e.preventDefault();
                        startListening();
                    }}
                    variant="ghost"
                >
                    <Mic className="h-5 w-5 text-destructive" />
                </Button>
            </TooltipWrapper>
        );
    }

    if (isMicrophoneTemporarilyDisabled) {
        return <DisabledMicContainer className={className} />;
    }

    const tooltipText = listening
        ? t("global.audioCapture.stopListening")
        : t("global.audioCapture.startListening");

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (listening) {
            stopListening();
        } else {
            startListening();
        }
    };

    return (
        <TooltipWrapper tooltipText={tooltipText}>
            <Button
                className={cn(
                    "h-11 w-11 rounded-full p-0 [&_svg]:size-5",
                    listening && "border-2 border-purple-500",
                    className,
                )}
                onClick={handleClick}
                variant="ghost"
            >
                <Mic className="h-5 w-5" />
            </Button>
        </TooltipWrapper>
    );
};

export default MicrophoneButton;
